<template>
    <v-dialog v-model="dialog" width="1400" :fullscreen="isMobileOrTablet()" persistent>
        <v-card class="rounded-lg" style="display: flex; flex-direction: column; height: 100%;">
            <v-card-title style="background-color: #1a5b0f !important; display: flex; justify-content: space-between; align-items: center;">
                <div>
                    <span class="font-weight-bold text-h6 text-white">{{ dialog_title }}</span>
                    <div class="text-caption text-white">{{ dialog_sub_title }}</div>
                </div>
                <v-btn v-if="loading == false" flat icon color="darkGreen" @click="closeDialog()">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="overflow-y: auto; max-height: calc(100vh - 100px);">
                <v-row>

                <!-- LEFT SIDE -->
                <v-col cols="12" md="8">
                    <v-form ref="form" class="mt-2">
                        <v-row dense>

                            <v-col v-if="action == 'add'" cols="12">
                                <v-autocomplete
                                    v-model="program_id"
                                    @update:model-value="loadCourse"
                                    label="Program / Department"
                                    :rules="[formRules.required]"
                                    variant="outlined" rounded="lg" color="darkGreen"
                                    :items="programData" item-title="name" item-value="id"
                                    :readonly="action == 'view'" />
                            </v-col>

                            <v-col v-if="action == 'add'" cols="12" md="4">
                                <v-autocomplete
                                    v-model="code"
                                    @update:model-value="loadCodeAndTitle"
                                    label="Course Code"
                                    :rules="[formRules.required]"
                                    variant="outlined" rounded="lg" color="darkGreen"
                                    :items="courseData" item-title="code" item-value="id"
                                    return-object :readonly="action == 'view'" />
                            </v-col>

                            <v-col v-else cols="12" md="4">
                                <v-text-field
                                    v-model="code" label="Course Code"
                                    :rules="[formRules.required]"
                                    variant="outlined" rounded="lg" color="darkGreen" />
                            </v-col>

                            <v-col cols="12" md="8">
                                <v-text-field
                                    v-model="title" label="Course Title"
                                    :rules="[formRules.required]"
                                    variant="outlined" rounded="lg" color="darkGreen" />
                            </v-col>

                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="unit" label="Unit"
                                    :rules="[formRules.required]"
                                    variant="outlined" rounded="lg" type="number" color="darkGreen"
                                    :readonly="action == 'view'" />
                            </v-col>

                            <v-col cols="12">
                                <div class="text-caption text-medium-emphasis mb-1 ml-1">Schedule Time</div>

                                <div
                                    v-for="(block, idx) in timeBlocks"
                                    :key="idx"
                                    class="d-flex align-center gap-2 mb-2"
                                >
                                    <v-text-field
                                        v-model="block.startTime"
                                        @update:model-value="rebuildTime"
                                        :label="timeBlocks.length > 1 ? `Block ${idx+1} Start` : 'Start Time'"
                                        placeholder="8:00"
                                        :rules="[formRules.required, validateHHMM]"
                                        variant="outlined" rounded="lg" color="darkGreen"
                                        style="max-width: 130px;"
                                        :readonly="action == 'view'" />

                                    <v-select
                                        v-model="block.startMeridiem"
                                        @update:model-value="rebuildTime"
                                        :items="['AM','PM']"
                                        variant="outlined" rounded="lg" color="darkGreen"
                                        style="max-width: 90px;"
                                        :readonly="action == 'view'" />

                                    <span class="text-medium-emphasis px-1">–</span>

                                    <v-text-field
                                        v-model="block.endTime"
                                        @update:model-value="rebuildTime"
                                        :label="timeBlocks.length > 1 ? `Block ${idx+1} End` : 'End Time'"
                                        placeholder="10:00"
                                        :rules="[formRules.required, validateHHMM]"
                                        variant="outlined" rounded="lg" color="darkGreen"
                                        style="max-width: 130px;"
                                        :readonly="action == 'view'" />

                                    <v-select
                                        v-model="block.endMeridiem"
                                        @update:model-value="rebuildTime"
                                        :items="['AM','PM','NN']"
                                        variant="outlined" rounded="lg" color="darkGreen"
                                        style="max-width: 90px;"
                                        :readonly="action == 'view'" />

                                    <v-text-field
                                        v-model="block.label"
                                        @update:model-value="rebuildTime"
                                        label="Label (optional)"
                                        placeholder="LAB"
                                        variant="outlined" rounded="lg" color="darkGreen"
                                        style="max-width: 140px;"
                                        hint="Lab or Lec"
                                        :readonly="action == 'view'" />

                                    <v-btn
                                        v-if="action != 'view' && timeBlocks.length > 1"
                                        icon size="small" variant="text" color="red"
                                        @click="removeTimeBlock(idx)">
                                        <v-icon>mdi-close-circle</v-icon>
                                    </v-btn>
                                </div>

                                <v-btn
                                    v-if="action != 'view'"
                                    size="small" variant="text" color="darkGreen"
                                    prepend-icon="mdi-plus"
                                    @click="addTimeBlock">
                                    Add another time block
                                </v-btn>

                                <v-text-field
                                    v-model="time"
                                    style="display:none"
                                    :rules="[formRules.required]" />
                            </v-col>

                            <!-- ── DAYS: checkboxes ── -->
                            <v-col cols="12" class="mb-6">
                                <div class="text-caption text-medium-emphasis mb-1 ml-1">Days</div>
                                <div class="d-flex flex-wrap gap-2">
                                    <v-chip
                                        v-for="d in dayOptions"
                                        :key="d.value"
                                        :color="selectedDays.includes(d.value) ? 'darkGreen' : ''"
                                        :variant="selectedDays.includes(d.value) ? 'elevated' : 'outlined'"
                                        class="cursor-pointer"
                                        :class="{ 'text-white': selectedDays.includes(d.value) }"
                                        @click="action != 'view' && toggleDay(d.value)"
                                    >
                                        {{ d.label }}
                                    </v-chip>
                                </div>
                                <v-text-field
                                    v-model="day"
                                    style="display:none"
                                    :rules="[formRules.required]" />
                                <div v-if="!day && dayTouched" class="text-caption text-red mt-1 ml-1">
                                    Please select at least one day.
                                </div>
                            </v-col>

                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="room" :rules="[formRules.required]"
                                    label="Room" variant="outlined" rounded="lg" color="darkGreen"
                                    :readonly="action == 'view'" />
                            </v-col>

                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="section_code" :rules="[formRules.required]"
                                    label="Section Code" hint="Ex. BSIT 1-A"
                                    variant="outlined" rounded="lg" color="darkGreen"
                                    :readonly="action == 'view'" />
                            </v-col>

                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="class_size" :rules="[formRules.required]"
                                    label="Class Size" variant="outlined" rounded="lg" type="number" color="darkGreen"
                                    :readonly="action == 'view'" />
                            </v-col>

                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="hours_lecture" label="Lecture Hours (a)"
                                    variant="outlined" rounded="lg" type="number" color="darkGreen"
                                    :readonly="action == 'view'" />
                            </v-col>

                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="hours_lab" label="Lab Hours"
                                    variant="outlined" rounded="lg" type="number" color="darkGreen"
                                    :readonly="action == 'view'" />
                            </v-col>

                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="actual_contact_teaching" readonly
                                    label="Actual Contact Teaching Hrs/Wk"
                                    variant="outlined" rounded="lg" color="darkGreen" />
                            </v-col>

                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="eqv" readonly label=".75 Eqv. (b)"
                                    variant="outlined" rounded="lg" type="number" color="darkGreen" />
                            </v-col>

                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="total_eqv" readonly label="Total Load Eqv."
                                    variant="outlined" rounded="lg" type="number" color="darkGreen" />
                            </v-col>

                            <v-col cols="12" md="4">
                                <v-autocomplete
                                    v-model="course_type" :rules="[formRules.required]"
                                    label="Course Type" variant="outlined" rounded="lg" color="darkGreen"
                                    :items="courseTypeData" item-title="name" item-value="name"
                                    :readonly="action == 'view'" />
                            </v-col>

                            <v-col v-if="course_type == 'Graduate School'" cols="12" md="4">
                                <v-autocomplete
                                    v-model="set_id" label="Set"
                                    :rules="[formRules.required]"
                                    variant="outlined" rounded="lg" color="darkGreen"
                                    :items="courseSetData" item-title="set_code" item-value="id" />
                            </v-col>

                        </v-row>
                    </v-form>
                </v-col>

                <!-- RIGHT SIDE — conflict panel -->
                <v-col cols="12" md="4">
                    <v-card
                        class="rounded-lg"
                        variant="outlined"
                        elevation="0"
                        style="position: sticky; top: 0;"
                    >
                        <!-- HEADER -->
                        <div class="pa-4" style="background-color: #f5f5f5; border-bottom: 1px solid #e0e0e0;">
                            <div class="d-flex justify-space-between align-center">
                                <div>
                                    <div class="font-weight-bold text-subtitle-1">Schedule Conflicts</div>
                                    <div class="text-caption text-medium-emphasis">Real-time conflict detection</div>
                                </div>
                                <v-chip :color="conflicts.length ? 'red' : 'green'" size="small">
                                    {{ conflicts.length }}
                                </v-chip>
                            </div>
                        </div>

                        <!-- BODY -->
                        <div style="max-height: 650px; overflow-y: auto;">

                            <div v-if="checkingConflict" class="pa-6 text-center">
                                <v-progress-circular indeterminate color="darkGreen" />
                            </div>

                            <!-- NO CONFLICT -->
                            <div v-else-if="conflicts.length == 0" class="pa-6 text-center">
                                <v-icon size="50" color="green" class="mb-2">mdi-check-circle-outline</v-icon>
                                <div class="font-weight-medium">No Conflicts Detected</div>
                                <div class="text-caption text-medium-emphasis">Current schedule is clear.</div>
                            </div>

                            <!-- CONFLICTS -->
                            <div
                                v-for="(item, index) in conflicts"
                                :key="index"
                                class="pa-3"
                                style="border-bottom: 1px solid #eeeeee;"
                            >
                                <div class="d-flex align-center mb-1">
                                    <v-icon color="red" size="20" class="mr-2">mdi-alert-circle</v-icon>
                                    <span class="font-weight-bold text-red mr-2">Schedule Conflict</span>
                                    <!-- Self vs Others badge -->
                                    <v-chip
                                        size="x-small"
                                        :color="item.conflict_type === 'self' ? 'orange' : 'red'"
                                        variant="elevated"
                                    >
                                        {{ item.conflict_type === 'self' ? 'Own Schedule' : 'Another Faculty' }}
                                    </v-chip>
                                </div>

                                <div class="text-body-2 mb-2 text-medium-emphasis">
                                    <span v-if="item.conflict_type === 'self'">
                                        This new schedule overlaps with your existing load.
                                    </span>
                                    <span v-else>
                                        Another faculty member already has this schedule.
                                    </span>
                                </div>

                                <v-card variant="tonal" class="pa-3 rounded-lg">
                                    <div class="mb-1"><strong>Course:</strong> {{ item.code }}</div>
                                    <div class="mb-1"><strong>Section:</strong> {{ item.section_code }}</div>
                                    <div class="mb-1"><strong>Schedule:</strong> {{ item.day }} | {{ item.time }}</div>
                                    <div class="mb-1"><strong>Room:</strong> {{ item.room }}</div>
                                    <div v-if="item.conflict_type !== 'self'">
                                        <strong>Faculty:</strong> {{ item.faculty_name }}
                                    </div>
                                </v-card>
                            </div>

                        </div>
                    </v-card>
                </v-col>

            </v-row>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-3" style="margin-top: auto;">
            <v-spacer></v-spacer>
            <v-btn class="mr-1" :disabled="loading" text="Cancel" variant="outlined" elevation="1" @click="closeDialog()"></v-btn>
            <v-btn class="mr-1" v-if="action != 'view'" :loading="loading" text="Save" variant="elevated" elevation="1" color="darkGreen" @click="validateForm()"></v-btn>
        </v-card-actions>
    </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios';
import { defineComponent } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'

const DAY_OPTIONS = [
    { value: 'M',   label: 'Mon' },
    { value: 'T',   label: 'Tue' },
    { value: 'W',   label: 'Wed' },
    { value: 'Th',  label: 'Thu' },
    { value: 'F',   label: 'Fri' },
    { value: 'Sat', label: 'Sat' },
    { value: 'Sun', label: 'Sun' },
]

function buildDayString(selected) {
    return DAY_OPTIONS
        .filter(d => selected.includes(d.value))
        .map(d => d.value)
        .join('')
}

function parseDayString(dayStr) {
    if (!dayStr) return []
    const result = []
    let s = dayStr.replace(/[()&, ]/g, '')
    let i = 0
    while (i < s.length) {
        const two = s.substring(i, i + 2)
        const three = s.substring(i, i + 3)
        if (/^(Sat|sat)/i.test(three)) { result.push('Sat'); i += 3; continue }
        if (/^(Sun|sun)/i.test(three)) { result.push('Sun'); i += 3; continue }
        if (/^(Th|th)/i.test(two))     { result.push('Th');  i += 2; continue }
        if (/^(Sa|sa)/i.test(two))     { result.push('Sat'); i += 2; continue }
        if (/^(Su|su)/i.test(two))     { result.push('Sun'); i += 2; continue } 
        const ch = s[i].toUpperCase()
        if (ch === 'M') result.push('M')
        else if (ch === 'T') result.push('T')
        else if (ch === 'W') result.push('W')
        else if (ch === 'F') result.push('F')
        else if (ch === 'S') result.push('Sat')
        i++
    }
    return [...new Set(result)]
}
function buildTimeString(blocks) {
    return blocks
        .filter(b => b.startTime && b.endTime)
        .map(b => {
            const start = `${b.startTime} ${b.startMeridiem}`
            const end   = `${b.endTime} ${b.endMeridiem}`
            return b.label ? `${start} - ${end} (${b.label})` : `${start} - ${end}`
        })
        .join(' & ')
}
function parseTimeString(timeStr) {
    if (!timeStr) return [defaultBlock()]

    // Split on & or ;
    const segments = timeStr.split(/[;&]/).map(s => s.trim()).filter(Boolean)

    return segments.map(seg => {
        // Extract optional label
        const labelMatch = seg.match(/\(([^)]+)\)\s*$/)
        const label = labelMatch ? labelMatch[1] : ''
        const clean = seg.replace(/\([^)]*\)/g, '').trim()

        const rangeRe = /(\d{1,2}:\d{2})\s*(AM|PM)?\s*[-–]\s*(\d{1,2}:\d{2})\s*(AM|PM|NN)?/i
        const m = clean.match(rangeRe)
        if (!m) return defaultBlock()

        let [, startTime, startMeridiem, endTime, endMeridiem] = m
        endMeridiem = endMeridiem?.toUpperCase() || 'PM'
        if (!startMeridiem) {
            const sh = parseInt(startTime.split(':')[0])
            const eh = parseInt(endTime.split(':')[0])
            startMeridiem = (sh > eh && endMeridiem === 'PM') ? 'AM' : endMeridiem
        } else {
            startMeridiem = startMeridiem.toUpperCase()
        }

        return { startTime, startMeridiem, endTime, endMeridiem, label }
    })
}

function defaultBlock() {
    return { startTime: '', startMeridiem: 'AM', endTime: '', endMeridiem: 'PM', label: '' }
}

export default defineComponent({
    emits: ["refresh"],
    data() {
        return {
            dialog: false,
            dialog_title: null,
            dialog_sub_title: null,
            action: null,
            loading: false,

            id: null,
            code: null,
            title: null,
            section_code: null,
            time: null,           // canonical string stored in DB
            day: null,            // canonical string stored in DB
            room: null,
            unit: null,
            class_size: null,
            hours_lecture: null,
            hours_lab: null,
            user_id: null,
            course_type: null,
            loaded_by: null,
            set_id: null,

            // UI state for structured inputs
            timeBlocks: [defaultBlock()],
            selectedDays: [],
            dayTouched: false,

            program_id: null,
            course: null,

            programData: [],
            courseData: [],
            courseSetData: [],

            dayOptions: DAY_OPTIONS,

            courseTypeData: [
                { name: 'Major Subject' },
                { name: 'General Education' },
                { name: 'Professional Education' },
                { name: 'Graduate School' },
            ],

            conflicts: [],
            checkingConflict: false,
        }
    },

    computed: {
        actual_contact_teaching() {
            return parseFloat(this.hours_lecture || 0) + parseFloat(this.hours_lab || 0)
        },
        eqv() {
            return this.hours_lab ? this.hours_lab * 0.75 : 0
        },
        total_eqv() {
            return parseFloat(this.hours_lecture || 0) + this.eqv
        }
    },

    watch: {
        time()         { this.checkScheduleConflict() },
        day()          { this.checkScheduleConflict() },
        room()         { this.checkScheduleConflict() },
        section_code() { this.checkScheduleConflict() },
    },

    methods: {
        validateHHMM(v) {
            if (!v) return true // required rule handles empty
            return /^\d{1,2}:\d{2}$/.test(v.trim()) || 'Use H:MM format (e.g. 8:00)'
        },

        addTimeBlock() {
            this.timeBlocks.push(defaultBlock())
        },

        removeTimeBlock(idx) {
            this.timeBlocks.splice(idx, 1)
            this.rebuildTime()
        },

        rebuildTime() {
            this.time = buildTimeString(this.timeBlocks)
        },
        toggleDay(val) {
            this.dayTouched = true
            const idx = this.selectedDays.indexOf(val)
            if (idx === -1) this.selectedDays.push(val)
            else this.selectedDays.splice(idx, 1)
            this.day = buildDayString(this.selectedDays)
            this.checkScheduleConflict()
        },
        async initAdd() {
            this.resetForm()
            this.dialog = true
            this.dialog_title = 'Course Load'
            this.dialog_sub_title = 'Add new course load'
            this.action = 'add'
            this.user_id = this.$store.state.user.id
            await this.loadProgram()
            await this.loadCourseSet()
            this.program_id = this.$store.state.user.program.id
            await this.loadCourse()
        },

        async initProgChairAdd(userId, programId) {
            this.resetForm()
            this.dialog = true
            this.dialog_title = 'Course Load'
            this.dialog_sub_title = 'Add new course load'
            this.action = 'add'
            this.user_id = userId
            await this.loadProgram()
            await this.loadCourseSet()
            this.program_id = programId
            await this.loadCourse()
        },

        async initDirectorAdd(userId, programId) {
            this.resetForm()
            this.dialog = true
            this.dialog_title = 'Course Load'
            this.dialog_sub_title = 'Add new course load'
            this.action = 'add'
            this.user_id = userId
            await this.loadProgram()
            await this.loadCourseSet()
            this.program_id = programId
            await this.loadCourse()
        },

        async initUpdate(data) {
            this.resetForm()
            this.dialog = true
            this.dialog_title = 'Course Load'
            this.dialog_sub_title = 'Update course load details'
            this.action = 'edit'
            this.fillForm(data)
            await this.loadCourseSet()
        },

        async initView(data) {
            this.resetForm()
            this.dialog = true
            this.dialog_title = 'Course Load'
            this.dialog_sub_title = 'View course load details'
            this.action = 'view'
            this.fillForm(data)
            await this.loadCourseSet()
        },

        fillForm(data) {
            this.id           = data.id
            this.code         = data.code
            this.title        = data.title
            this.section_code = data.section_code
            this.time         = data.time
            this.day          = data.day
            this.room         = data.room
            this.unit         = data.unit
            this.class_size   = data.class_size
            this.hours_lecture = data.hours_lecture
            this.hours_lab    = data.hours_lab
            this.user_id      = data.user_id
            this.course_type  = data.course_type
            this.program_id   = data.program_id
            this.set_id       = data.set_id

            this.timeBlocks  = parseTimeString(data.time)
            this.selectedDays = parseDayString(data.day)
        },

        resetForm() {
            this.id = null
            this.code = null; this.title = null; this.section_code = null
            this.time = null; this.day = null; this.room = null
            this.unit = null; this.class_size = null
            this.hours_lecture = null; this.hours_lab = null
            this.user_id = null; this.course_type = null
            this.program_id = null; this.set_id = null
            this.timeBlocks = [defaultBlock()]
            this.selectedDays = []
            this.dayTouched = false
            this.conflicts = []
        },

        async loadProgram() {
            const res = await axios.get(process.env.VUE_APP_SERVER_URL + '/programs')
            this.programData = res.data
        },

        async loadCourseSet() {
            const res = await this.axiosCall(`/course-set`, 'get', true)
            if (res.data) this.courseSetData = res.data
        },

        async loadCourse() {
            this.code = null; this.title = null
            const res = await axios.get(process.env.VUE_APP_SERVER_URL + '/course/findAllActiveCourseByProgram/' + this.program_id)
            this.courseData = res.data
        },

        loadCodeAndTitle(data) {
            if (data) {
                this.code  = data.code
                this.title = data.title
                this.unit  = data.unit
                this.hours_lab     = data.hours_lab != 0 ? data.hours_lab * 3 : 0
                this.hours_lecture = data.hours_lecture
            }
        },

        async validateForm() {
            const { showSnackbar } = useSnackbar()
            this.dayTouched = true

            const { valid } = await this.$refs.form.validate()
            if (!this.day) return // day chips not selected

            if (valid) {
                this.loading = true
                const formData = {
                    id: this.id,
                    code: this.code, title: this.title,
                    section_code: this.section_code,
                    time: this.time, day: this.day, room: this.room,
                    unit: this.unit, class_size: this.class_size,
                    hours_lecture: this.hours_lecture, hours_lab: this.hours_lab,
                    actual_contact_teaching: this.actual_contact_teaching,
                    eqv: this.eqv, total_eqv: this.total_eqv,
                    semester_schoolyear_id: this.$store.state.semester_schoolyear.id,
                    user_id: this.user_id, course_type: this.course_type,
                    program_id: this.program_id,
                    loaded_by: this.$store.state.user.id,
                    set_id: this.set_id,
                }

                const token = localStorage.getItem('aums-token')
                const method = this.action === 'add' ? 'post' : 'patch'
                const url = process.env.VUE_APP_SERVER_URL + '/faculty-load'

                try {
                    const res = await axios[method](url, formData, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                    if (res.data.status == 201) {
                        this.loading = false
                        this.$emit('refresh')
                        showSnackbar(`Subject Load successfully ${this.action === 'add' ? 'added' : 'updated'}!`)
                        this.closeDialog()
                    }
                } catch (error) {
                    this.loading = false
                    if (error.response?.status == 401) {
                        showSnackbar('Token expired! Logging out..')
                        localStorage.removeItem("vuex")
                        localStorage.removeItem("aums-token")
                        window.location.reload()
                    } else {
                        console.error(error)
                    }
                }
            }
        },
        async checkScheduleConflict() {
            if (!this.time || !this.day) {
                this.conflicts = []
                return
            }

            try {
                this.checkingConflict = true
                const token = localStorage.getItem('aums-token')

                const res = await axios.post(
                    process.env.VUE_APP_SERVER_URL + '/faculty-load/check-conflict',
                    {
                        id: this.id,
                        time: this.time,
                        day: this.day,
                        room: this.room,
                        section_code: this.section_code,
                        user_id: this.user_id,
                        semester_schoolyear_id: this.$store.state.semester_schoolyear.id
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                )
                this.conflicts = res.data.conflicts || []

            } catch (error) {
                console.error(error)
            } finally {
                this.checkingConflict = false
            }
        },

        // ── Close ─────────────────────────────────────────────────────────────

        closeDialog() {
            if (this.$refs.form) this.$refs.form.reset()
            this.resetForm()
            this.dialog = false
        }
    }
})
</script>

<style scoped>
.cursor-pointer { cursor: pointer; }
.gap-2 { gap: 8px; }
</style>